import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePortal, type ClientDetail as ClientDetailDto, type UpdateClientBody } from "@app/portal-sdk";
import { Alert, AppBadge, Box, Button, Snackbar, Stack, Typography } from "@app/ui-kit";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { LoadingScreen } from "../components/LoadingScreen";
import { RecordActions } from "../components/RecordActions";
import { errorMessage, useApiQuery } from "../lib/useApiQuery";
import { useShell } from "../components/ShellContext";
import { OverviewTab } from "./tabs/OverviewTab";
import { MembersTab } from "./tabs/MembersTab";
import { AppsTab } from "./tabs/AppsTab";
import { ActivityTab } from "./tabs/ActivityTab";
import { SettingsTab } from "./tabs/SettingsTab";

const TABS = ["overview", "members", "apps", "settings", "activity"] as const;
type TabKey = (typeof TABS)[number];

export const ClientDetail = () => {
  const { clientId = "" } = useParams();
  const navigate = useNavigate();
  const { api } = usePortal();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: TabKey = (TABS as readonly string[]).includes(tabParam ?? "") ? (tabParam as TabKey) : "overview";

  const client = useApiQuery<ClientDetailDto>(
    () => api.request<ClientDetailDto>(`/admin/clients/${clientId}`),
    [api, clientId]
  );
  // MNT-2: the Clients section names the client that is open, beneath All clients, and forgets it on the way out.
  const { setAdminClient } = useShell();
  const clientName = client.data?.name ?? null;
  useEffect(() => { setAdminClient(clientName ? { id: clientId, name: clientName } : null); return () => setAdminClient(null); }, [clientId, clientName, setAdminClient]);
  const [confirmStatus, setConfirmStatus] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (client.loading && !client.data) return <LoadingScreen />;
  if (client.error && !client.data) return <Alert severity="error">{client.error}</Alert>;
  const data = client.data;
  if (!data) return null;

  const suspended = data.status === "suspended";
  const portalLink = `${window.location.origin}/c/${data.slug}`;

  // ACT-2: the link the eyebrow used to print. Copying confirms; failing says what the link is.
  const copyPortalLink = async () => {
    try {
      await navigator.clipboard.writeText(portalLink);
      setCopied(true);
    } catch {
      setError(`The portal link could not be copied. It is ${portalLink}`);
    }
  };

  const toggleStatus = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: UpdateClientBody = { status: suspended ? "active" : "suspended" };
      const updated = await api.request<ClientDetailDto>(`/admin/clients/${data.id}`, { method: "PATCH", json: body });
      client.setData(updated);
      setConfirmStatus(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={4}>
      <Box sx={{ pb: 3, borderBottom: "1.5px solid", borderColor: "divider" }}>
        <Button variant="text" size="small" onClick={() => navigate("/admin")} sx={{ mb: 2, ml: -1 }}>
          ← All clients
        </Button>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }} justifyContent="space-between">
          <Stack direction="row" spacing={3} alignItems="center">
            {data.logoUrl && (
              <Box component="img" src={data.logoUrl} alt={data.name} sx={{ height: 48, width: "auto", maxWidth: 160 }} />
            )}
            <Box>
              {/* ACT-2: status is a property of the client, so it sits on the eyebrow line, not in the action row. */}
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="overline" sx={{ color: "secondary.main" }}>
                  Client · /c/{data.slug}
                </Typography>
                <AppBadge label={suspended ? "Suspended" : "Active"} color={suspended ? "warning" : "success"} />
              </Stack>
              <Typography variant="h3" sx={{ color: "text.primary" }}>
                {data.name}
              </Typography>
            </Box>
          </Stack>
          {/* ACT-2: one primary, the thing this page is opened to do; the rest behind More actions. Opening the
              portal is audited by the API exactly as before. */}
          <RecordActions
            primary={{ label: "Open portal", onClick: () => navigate(`/c/${data.slug}`) }}
            items={[
              { label: "Copy portal link", onClick: () => void copyPortalLink(), tooltip: portalLink },
              { label: suspended ? "Reactivate" : "Suspend", destructive: !suspended, onClick: () => setConfirmStatus(true) }
            ]}
          />
        </Stack>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Snackbar open={copied} autoHideDuration={3000} onClose={() => setCopied(false)} message="Portal link copied" />

      <Tabs
        value={tab}
        onChange={(_, value: TabKey) => setSearchParams(value === "overview" ? {} : { tab: value })}
        sx={{ borderBottom: "1.5px solid", borderColor: "divider" }}
      >
        <Tab value="overview" label="Overview" />
        <Tab value="members" label="Members" />
        <Tab value="apps" label="Apps" />
        <Tab value="settings" label="Settings" />
        <Tab value="activity" label="Activity" />
      </Tabs>

      {tab === "overview" && <OverviewTab client={data} onChange={client.setData} />}
      {tab === "members" && <MembersTab client={data} />}
      {tab === "apps" && <AppsTab client={data} onChange={() => void client.reload()} />}
      {tab === "settings" && <SettingsTab client={data} onChange={() => void client.reload()} />}
      {tab === "activity" && <ActivityTab client={data} />}

      <ConfirmDialog
        open={confirmStatus}
        title={suspended ? `Reactivate ${data.name}?` : `Suspend ${data.name}?`}
        description={
          suspended
            ? "Members will be able to sign in and use the portal again."
            : "Members will be signed out and cannot open the portal until it is reactivated. Files and settings are kept."
        }
        confirmLabel={suspended ? "Reactivate" : "Suspend"}
        destructive={!suspended}
        busy={busy}
        onConfirm={() => void toggleStatus()}
        onClose={() => setConfirmStatus(false)}
      />
    </Stack>
  );
};
